import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Grid,
  Typography,
  AppBar,
  Avatar,
  Popover,
  Box,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import LanguageIcon from "@mui/icons-material/Language";
import MapsHomeWorkIcon from "@mui/icons-material/MapsHomeWork";
import Drawer from "../main/Drawer";
import { themeMain } from "../components/wrapper/CThemeProvider";
import LanguageSelector from "./LanguageSelector";

const Header = ({
  cLogo,
  cLogoSmall,
  handleLogout,
  cUserName,
  cUserInitials,
  pageTitle,
  appRouteMap,
  drawerList,
  pathName,
  navigate,
  linkComponent,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm")); 
  const isSm = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isSmallScreen = isXs || isSm; 
  const gapValue = isXs ? 0.5 : isSm ? 0.5 : 1; 
  const terminal_id = localStorage.getItem("terminal");
  const terminal_name = localStorage.getItem("terminal_name");
  const [profilePopover, setProfilePopover] = useState(null);
  const allLanguages = {
    en: "English",
    fr: "Français",
    es: "Español",
    ar: "Arabic",
    fil: "Filipino",
    tpi: "Tok Pisin",
    pt: "Portuguese",
    id: "Indonesian",
  };
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    return localStorage.getItem("is_language") || "en";
  });
  useEffect(() => {
    const interval = setInterval(() => {
      const storedLang = localStorage.getItem("is_language") || "en";
      setCurrentLanguage((prev) => (prev !== storedLang ? storedLang : prev));
    }, 1000); 
    return () => clearInterval(interval);
  }, []);
  const handlePopOpen = (event) => {
    setProfilePopover(event.currentTarget);
  };

  const handlePopClose = () => {
    setProfilePopover(null);
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: themeMain.palette.mainbg.main,
          padding: "0.5em 1em",
        }}
      >
        <Grid container alignItems="center">
          <Grid item xs={1} sm={1} md={1} lg={0.5} xl={0.5}>
            <Drawer
              appRouteMap={appRouteMap}
              drawerList={drawerList}
              pathName={pathName}
              linkComponent={linkComponent}
            />
          </Grid>
          <Grid item xs={1} sm={2} md={2} lg={2} xl={1.5}>
            <Box
              component="img"
              src={isXs ? cLogoSmall : cLogo}
              alt="Click to see All Services"
              onClick={() => navigate("/services")}
              sx={{
                maxWidth: "80%",
                maxHeight: "100%",
                cursor: "pointer",
                objectFit: "contain",
                height: "1.6em",
                width: "auto",
                marginLeft: ".2em",
              }}
            />
          </Grid>

          {!isSmallScreen && (
            <Grid
              item
              md={6}
              lg={6.5}
              xl={7}
              sx={{
                textAlign: "center",
                overflow: "hidden",
              }}
            >
              <Typography
                color="secondary"
                sx={{
                  fontSize: {
                    xs: "1rem",
                    sm: "1rem",
                    md: "1.2rem",
                    lg: "1.4rem",
                    xl: "1.4rem",
                  },
                  whiteSpace: "nowrap",
                }}
              >
                {t(`${pageTitle}`)}
              </Typography>
            </Grid>
          )}

          <Grid
            item
            xs={10}
            sm={9}
            md={3}
            lg={3}
            xl={3}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            {cUserName && (
              <Box display="flex" alignItems="center" gap={gapValue}>
                <LanguageSelector />
                <Avatar
                  sx={{
                    padding: {
                      xs: "0.1em",
                      sm: "0.1em",
                      md: "0.2em",
                      lg: "0.2em",
                      xl: "0.2em",
                    },
                    bgcolor: themeMain.palette.secondary.main,
                    fontWeight: "bold",
                    cursor: "pointer",
                    fontSize: {
                      xs: "0.8rem",
                      sm: "1.1rem",
                      md: "1.3rem",
                      lg: "1.2rem",
                      xl: "1.2rem",
                    },
                    width: {
                      xs: "2.2em",
                      sm: "2em",
                      md: "1.8em",
                      lg: "1.5em",
                      xl: "1.5em",
                    },
                    height: {
                      xs: "2.2em",
                      sm: "2em",
                      md: "1.8em",
                      lg: "1.5em",
                      xl: "1.5em",
                    },
                    transition: "background-color 0.3s ease, transform 0.2s",
                    "&:hover": {
                      backgroundColor: themeMain.palette.secondary.dark,
                      transform: "scale(1.05)",
                    },
                  }}
                  onClick={handlePopOpen}
                >
                  {cUserInitials}
                </Avatar>

                <Popover
                  sx={{ marginTop: "0.5em" }}
                  open={Boolean(profilePopover)}
                  anchorEl={profilePopover}
                  onClose={handlePopClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                  }}
                >
                  <Box sx={{ padding: "1.5em", width: "170px" }}>
                    <Box display="flex" alignItems="center" mb={2}>
                      <PersonIcon
                        sx={{ marginRight: "0.5em", color: "#052660" }}
                        fontSize="small"
                      />
                      <Typography variant="subtitle1">{cUserName}</Typography>
                    </Box>

                    <Box display="flex" alignItems="center" mb={2}>
                      <MapsHomeWorkIcon
                        sx={{ marginRight: "0.5em", color: "#052660" }}
                        fontSize="small"
                      />
                      <Typography variant="subtitle1">
                        {terminal_name} - {terminal_id}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" mb={2}>
                      <LanguageIcon
                        sx={{ marginRight: "0.5em", color: "#052660" }}
                        fontSize="small"
                      />
                      <Typography variant="subtitle1">
                        {allLanguages[currentLanguage] || "English"}
                      </Typography>
                    </Box>
                    <Box
                      display="flex"
                      alignItems="center"
                      mt={3}
                      onClick={handleLogout}
                      sx={{ cursor: "pointer" }}
                    >
                      <LogoutIcon
                        fontSize="small"
                        style={{
                          color: "#052660",
                          borderRadius: 5,
                          paddingRight: "10px",
                        }}
                      />
                      <Typography
                        variant="button"
                        sx={{
                          cursor: "pointer",
                          fontWeight: "bold",
                          color: "#052660",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {t("logout")}
                      </Typography>
                    </Box>
                  </Box>
                </Popover>
              </Box>
            )}
          </Grid>
          {isSmallScreen && (
            <Box
              sx={{
                backgroundColor: themeMain.palette.mainbg.main,
                width: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "0.45em 0em",
                }}
              >
                <Typography
                  color="secondary"
                  sx={{
                    fontSize: {
                      xs: "1rem",
                      sm: "1rem",
                      md: "1.2rem",
                      lg: "1.4rem",
                      xl: "1.4rem",
                    },
                    whiteSpace: "nowrap",
                  }}
                >
                  {t(`${pageTitle}`)}
                </Typography>
              </Box>
            </Box>
          )}
        </Grid>
      </AppBar>
    </>
  );
};

export default Header;
